import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="7690" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-primary/10 animate-pulse rounded-full" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:text-accent font-sans">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-accent transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-4 text-primary/70">Loading menu...</div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium uppercase tracking-wide text-primary hover:text-accent" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-4 w-full bg-accent text-white hover:bg-accent/90 font-sans uppercase tracking-widest rounded-full">
        <Link to="https://bodywerkscy.setmore.com" newTab onClick={onClose}>
          Book Now
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="bg-background text-foreground relative z-50">
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md shadow-md border-primary/5" : "bg-background border-transparent")}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-3 items-center py-6">
            
            <div className="flex items-center justify-start md:hidden col-span-1">
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-primary hover:text-accent hover:bg-primary/5" aria-label="Toggle menu">
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
            
            <div className="flex flex-col items-center justify-center col-span-3">
              <div className="flex justify-center mb-2">
                <Link to="/HomePage" className="flex items-center justify-center">
                  <img src="https://immune-albacore-dev.10web.cloud/wp-content/uploads/2026/03/BODYWERKS-LOGO-COLOR-NO-BGR-scaled.png" alt="Bodywerks Logo" className="w-auto object-contain" />
                </Link>
              </div>

              <div className="flex items-center justify-center gap-8 w-full">
                <div className="hidden md:block">
                  <MenuProvider menu_id="37">
                    <DesktopMenuConsumer />
                  </MenuProvider>
                </div>
                <Button asChild className="hidden md:inline-flex bg-accent text-white hover:bg-accent/90 font-sans font-bold uppercase tracking-widest text-xs px-6 h-10 shadow-sm rounded-full transition-all duration-300 hover:scale-105">
                  <Link to="https://bodywerkscy.setmore.com" newTab>BOOK NOW</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className={cn("absolute left-0 top-full w-full overflow-hidden bg-background/95 backdrop-blur-xl transition-all duration-300 md:hidden", isMobileMenuOpen ? "max-h-screen border-t border-primary/10" : "max-h-0")}>
          <MenuProvider menu_id="37">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </header>
    </section>;
}